import { CSSProperties, FC, useId, useState } from 'react';
import { Button, Col, Divider, Popover, Row, Space, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { TbEye, TbEyeOff, TbGripVertical, TbInfoCircle, TbSquareRounded, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    metricChartSeriesVisibilityKey,
    reorderMetricChartComparisonCompanies,
} from '@/shared/components/data-visualization/recharts';
import type { MetricChartCompany } from '@/shared/components/data-visualization/recharts';
import { DragDropContext, Draggable, Droppable, DropResult } from '@/shared/components/drag-and-drop';
import type { DraggableProvidedDragHandleProps } from '@/shared/components/drag-and-drop';
import FinancialCompanyAvatar from '@/shared/components/financial-statements/components/FinancialCompanyAvatar';
import FinancialSeriesToolbar from '@/shared/components/financial-statements/components/FinancialSeriesToolbar';
import type {
    FinancialSeriesSnapshot,
    FinancialStatementDefaults,
} from '@/shared/components/financial-statements/financialSeries';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import { TickerExchangeTag } from '@/shared/components/tags';
import type { SavedSeries, SetStateMethod } from '@/types';

import MetricLineItem from './components/MetricLineItem';

type MetricLineItemsView = 'company' | 'economic' | 'investment' | 'overview';

interface ComparedCompanySeriesButtonProps {
    company: MetricChartCompany;
    companyIndex: number;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    draggable: boolean;
    hiddenSeriesKeys: string[];
    isDragging: boolean;
    lineItemLabels?: Partial<Record<string, string>>;
    onHiddenSeriesKeysChange: SetStateMethod<string[]>;
    onRemoveCompany?: () => void;
    palette: Array<{ fill: string; stroke: string }>;
    setTags: (tags: string[]) => void;
    tags: string[];
}

const ComparedCompanySeriesButton: FC<ComparedCompanySeriesButtonProps> = ({
    company,
    companyIndex,
    dragHandleProps,
    draggable,
    hiddenSeriesKeys,
    isDragging,
    lineItemLabels,
    onHiddenSeriesKeysChange,
    onRemoveCompany,
    palette,
    setTags,
    tags,
}) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);
    const visibleTagCount = tags.filter(
        (tag) => !hiddenSeriesKeys.includes(metricChartSeriesVisibilityKey(company.symbol, tag)),
    ).length;
    const breakdown = (
        <div style={{ minWidth: '18rem' }}>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '0.5rem',
                    padding: '0.75rem',
                }}
            >
                <FinancialCompanyAvatar company={company} size='small' />
                <span style={{ minWidth: 0 }}>
                    <Typography.Text ellipsis strong style={{ display: 'block' }}>
                        {company.companyName}
                    </Typography.Text>
                    <span style={{ alignItems: 'center', display: 'flex', gap: '0.375rem', marginTop: '0.125rem' }}>
                        <TickerExchangeTag
                            companyName={company.companyName}
                            exchange={company.exchange}
                            styles={{ color: token.colorTextSecondary, marginLeft: 0 }}
                            symbol={company.symbol}
                            website={company.website}
                        />
                        <small style={{ color: token.colorTextTertiary }}>
                            {visibleTagCount === tags.length
                                ? `${tags.length} line item${tags.length === 1 ? '' : 's'}`
                                : `${visibleTagCount} of ${tags.length} shown`}
                        </small>
                    </span>
                </span>
            </div>
            <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                {tags.map((tag, lineItemIndex) => {
                    const color = palette[lineItemIndex % palette.length];
                    const seriesKey = metricChartSeriesVisibilityKey(company.symbol, tag);
                    const hidden = hiddenSeriesKeys.includes(seriesKey);

                    return (
                        <div
                            key={`${company.symbol}-${tag}`}
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '0.25rem',
                                justifyContent: 'space-between',
                                minHeight: '2rem',
                                padding: '0.125rem 0.375rem 0.125rem 0.75rem',
                            }}
                        >
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    gap: '0.375rem',
                                    minWidth: 0,
                                    opacity: hidden ? 0.5 : 1,
                                }}
                            >
                                <TbSquareRounded
                                    fill={color.fill}
                                    size='0.875rem'
                                    stroke={color.stroke}
                                    style={{ display: 'block', flex: 'none' }}
                                />
                                <Typography.Text ellipsis>{lineItemLabels?.[tag] ?? capitalCase(tag)}</Typography.Text>
                            </span>
                            <Space.Compact style={{ display: 'inline-flex', flex: 'none' }}>
                                <Button
                                    aria-label={`${hidden ? 'Show' : 'Hide'} ${capitalCase(tag)} for ${company.companyName}`}
                                    icon={
                                        hidden ? (
                                            <TbEye style={{ display: 'block' }} />
                                        ) : (
                                            <TbEyeOff style={{ display: 'block' }} />
                                        )
                                    }
                                    size='small'
                                    style={{
                                        alignItems: 'center',
                                        display: 'inline-flex',
                                        height: '1.5rem',
                                        justifyContent: 'center',
                                    }}
                                    title={`${hidden ? 'Show' : 'Hide'} series`}
                                    type='text'
                                    onClick={() =>
                                        onHiddenSeriesKeysChange((current) =>
                                            current.includes(seriesKey)
                                                ? current.filter((key) => key !== seriesKey)
                                                : [...current, seriesKey],
                                        )
                                    }
                                />
                                {tags.length > 1 ? (
                                    <Button
                                        aria-label={`Remove ${capitalCase(tag)} from chart`}
                                        icon={<TbX style={{ display: 'block' }} />}
                                        size='small'
                                        style={{
                                            alignItems: 'center',
                                            display: 'inline-flex',
                                            height: '1.5rem',
                                            justifyContent: 'center',
                                        }}
                                        title='Remove line item'
                                        type='text'
                                        onClick={() => setTags(tags.filter((item) => item !== tag))}
                                    />
                                ) : null}
                            </Space.Compact>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <span
            style={{ display: 'inline-flex' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Space.Compact
                style={{
                    boxShadow: isDragging ? token.boxShadowSecondary : undefined,
                    display: 'inline-flex',
                }}
            >
                <Popover
                    arrow={false}
                    content={breakdown}
                    mouseEnterDelay={0.2}
                    placement='bottomLeft'
                    styles={{ body: { padding: 0 } }}
                    trigger='hover'
                >
                    <Button size='small'>
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: '0.375rem',
                                height: '100%',
                                minWidth: 0,
                            }}
                        >
                            {draggable && (hovered || isDragging) ? (
                                <span
                                    {...dragHandleProps}
                                    aria-label={`Reorder ${company.companyName}`}
                                    role='button'
                                    style={{
                                        alignItems: 'center',
                                        color: token.colorTextTertiary,
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        display: 'inline-flex',
                                        flex: 'none',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <TbGripVertical style={{ display: 'block' }} />
                                </span>
                            ) : null}
                            <FinancialCompanyAvatar company={company} size={16} />
                            <span
                                style={{
                                    maxWidth: '8rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {company.companyName}
                            </span>
                            <span
                                aria-hidden
                                style={{ alignItems: 'center', display: 'inline-flex', gap: '0.1875rem' }}
                            >
                                {tags.map((tag, lineItemIndex) => {
                                    const color = palette[lineItemIndex % palette.length];
                                    const hidden = hiddenSeriesKeys.includes(
                                        metricChartSeriesVisibilityKey(company.symbol, tag),
                                    );

                                    return (
                                        <span
                                            key={`${company.symbol}-${tag}-segment`}
                                            style={{
                                                background: color.fill,
                                                border: `1px solid ${color.stroke}`,
                                                borderRadius: token.borderRadius,
                                                boxSizing: 'border-box',
                                                display: 'block',
                                                height: '0.75rem',
                                                opacity: hidden ? 0.25 : 1,
                                                width: '0.25rem',
                                            }}
                                        />
                                    );
                                })}
                            </span>
                        </span>
                    </Button>
                </Popover>
                {companyIndex > 0 && onRemoveCompany ? (
                    <Button
                        aria-label={`Remove ${company.companyName} from comparison`}
                        icon={<TbX style={{ color: token.colorTextTertiary, display: 'block' }} />}
                        size='small'
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            justifyContent: 'center',
                        }}
                        onClick={onRemoveCompany}
                    />
                ) : null}
            </Space.Compact>
        </span>
    );
};

interface MetricLineItemsProps {
    companies?: MetricChartCompany[];
    deletingSavedSeriesId?: string;
    financialDefaults?: FinancialStatementDefaults;
    financialSeriesSnapshot?: FinancialSeriesSnapshot;
    hiddenSeriesKeys?: string[];
    loadingSavedSeries?: boolean;
    lineItemLabels?: Partial<Record<string, string>>;
    onApplySeries?: (series: SavedSeries) => void;
    onComparisonCompaniesChange?: (companies: MetricChartCompany[]) => void;
    onCreateSeries?: (name: string, snapshot: FinancialSeriesSnapshot) => Promise<unknown>;
    onDeleteSeries?: (id: string) => Promise<unknown>;
    onUpdateSeries?: (id: string, name: string) => Promise<unknown>;
    onFinancialDefaultsChange?: (defaults: FinancialStatementDefaults) => void;
    onHiddenSeriesKeysChange?: SetStateMethod<string[]>;
    savedSeries?: SavedSeries[];
    savingSeries?: boolean;
    updatingSavedSeriesId?: string;
    setTags: (tags: string[]) => void;
    tags: string[];
    view?: MetricLineItemsView;
}

const MetricLineItems: FC<MetricLineItemsProps> = ({
    companies = [],
    deletingSavedSeriesId,
    financialDefaults,
    financialSeriesSnapshot,
    hiddenSeriesKeys = [],
    loadingSavedSeries = false,
    lineItemLabels,
    onApplySeries,
    onComparisonCompaniesChange,
    onCreateSeries,
    onDeleteSeries,
    onUpdateSeries,
    onFinancialDefaultsChange,
    onHiddenSeriesKeysChange,
    savedSeries = [],
    savingSeries = false,
    updatingSavedSeriesId,
    tags,
    setTags,
    view = 'economic',
}): JSX.Element => {
    const { token, chartColorPalettes, chartLineItemColors } = useAntDesignTheme();
    const droppableId = `metric-line-items-${useId().replace(/:/g, '')}`;
    const companyDroppableId = `${droppableId}-companies`;
    const hasTags = tags.length > 0;
    const comparingCompanies = companies.length > 1;
    const showFinancialSeriesToolbar = Boolean(
        financialDefaults &&
        financialSeriesSnapshot &&
        onApplySeries &&
        onCreateSeries &&
        onDeleteSeries &&
        onUpdateSeries &&
        onFinancialDefaultsChange,
    );

    const handleDragEnd = ({ destination, source }: DropResult) => {
        if (!destination || destination.index === source.index) {
            return;
        }

        const reorderedTags = [...tags];
        const [movedTag] = reorderedTags.splice(source.index, 1);

        reorderedTags.splice(destination.index, 0, movedTag);
        setTags(reorderedTags);
    };

    const handleCompanyDragEnd = ({ destination, source }: DropResult) => {
        if (!destination || source.index === 0 || destination.index === source.index || !onComparisonCompaniesChange) {
            return;
        }

        onComparisonCompaniesChange(reorderMetricChartComparisonCompanies(companies, source.index, destination.index));
    };

    const rowStyleByView: Record<MetricLineItemsView, CSSProperties> = {
        company: {
            borderBottom: `1px solid ${token.colorBorder}`,
            padding: '0.5rem 1.5rem',
        },
        economic: {
            borderBottom: `1px solid ${token.colorBorder}`,
            padding: '0.5rem 1.5rem',
        },
        investment: {
            borderBottom: `1px solid ${token.colorBorder}`,
            padding: '0.5rem 1.5rem',
        },
        overview: {
            borderBottom: `1px solid ${token.colorBorder}`,
            borderTop: `1px solid ${token.colorBorder}`,
            marginInline: '-1.25rem',
            marginTop: '0.75rem',
            padding: '0.5rem 1.25rem',
        },
    };

    return (
        <Row align='middle' wrap={false} style={rowStyleByView[view]}>
            <Col flex='auto'>
                {hasTags ? (
                    comparingCompanies ? (
                        <div style={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
                            <small style={{ color: token.colorTextTertiary, flex: 'none' }}>
                                <TbInfoCircle style={{ marginRight: '0.25rem', verticalAlign: '-0.1rem' }} />
                                Compared Series
                            </small>
                            <Divider type='vertical' />
                            <DragDropContext onDragEnd={handleCompanyDragEnd}>
                                <Droppable direction='horizontal' droppableId={companyDroppableId}>
                                    {(droppableProvided) => (
                                        <div
                                            ref={droppableProvided.innerRef}
                                            {...droppableProvided.droppableProps}
                                            style={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: '0.5rem',
                                                minWidth: 0,
                                                overflowX: 'auto',
                                                padding: '0.125rem 0',
                                            }}
                                        >
                                            {companies.map((company, companyIndex) => {
                                                const palette =
                                                    chartColorPalettes[companyIndex % chartColorPalettes.length];

                                                return (
                                                    <Draggable
                                                        draggableId={`${companyDroppableId}-${company.symbol}`}
                                                        index={companyIndex}
                                                        isDragDisabled={companyIndex === 0 || companies.length < 3}
                                                        key={company.symbol}
                                                    >
                                                        {(draggableProvided, snapshot) => (
                                                            <span
                                                                ref={draggableProvided.innerRef}
                                                                {...draggableProvided.draggableProps}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    flex: 'none',
                                                                    ...draggableProvided.draggableProps.style,
                                                                }}
                                                            >
                                                                <ComparedCompanySeriesButton
                                                                    company={company}
                                                                    companyIndex={companyIndex}
                                                                    dragHandleProps={draggableProvided.dragHandleProps}
                                                                    draggable={companyIndex > 0 && companies.length > 2}
                                                                    hiddenSeriesKeys={hiddenSeriesKeys}
                                                                    isDragging={snapshot.isDragging}
                                                                    lineItemLabels={lineItemLabels}
                                                                    onHiddenSeriesKeysChange={
                                                                        onHiddenSeriesKeysChange ?? (() => undefined)
                                                                    }
                                                                    palette={palette}
                                                                    setTags={setTags}
                                                                    tags={tags}
                                                                    onRemoveCompany={
                                                                        companyIndex > 0 && onComparisonCompaniesChange
                                                                            ? () =>
                                                                                  onComparisonCompaniesChange(
                                                                                      companies
                                                                                          .slice(1)
                                                                                          .filter(
                                                                                              (item) =>
                                                                                                  item.symbol !==
                                                                                                  company.symbol,
                                                                                          ),
                                                                                  )
                                                                            : undefined
                                                                    }
                                                                />
                                                            </span>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {droppableProvided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    ) : (
                        <>
                            <small style={{ color: token.colorTextTertiary, verticalAlign: '0.1rem' }}>
                                <TbInfoCircle style={{ marginRight: '0.25rem', verticalAlign: '-0.1rem' }} />
                                Line Items in Chart
                            </small>
                            <Divider type='vertical' />
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable direction='horizontal' droppableId={droppableId}>
                                    {(droppableProvided) => (
                                        <div
                                            ref={droppableProvided.innerRef}
                                            {...droppableProvided.droppableProps}
                                            style={{ display: 'inline-flex', verticalAlign: 'middle' }}
                                        >
                                            {tags.map((tag, index) => {
                                                const color = chartLineItemColors[index % chartLineItemColors.length];

                                                return (
                                                    <Draggable
                                                        key={tag}
                                                        draggableId={`${droppableId}-${tag}`}
                                                        index={index}
                                                        isDragDisabled={tags.length < 2}
                                                    >
                                                        {(draggableProvided, snapshot) => (
                                                            <span
                                                                ref={draggableProvided.innerRef}
                                                                {...draggableProvided.draggableProps}
                                                                style={draggableProvided.draggableProps.style}
                                                            >
                                                                <MetricLineItem
                                                                    dragHandleProps={draggableProvided.dragHandleProps}
                                                                    draggable={tags.length > 1}
                                                                    fill={color?.fill}
                                                                    isDragging={snapshot.isDragging}
                                                                    label={tag}
                                                                    displayLabel={lineItemLabels?.[tag]}
                                                                    onRemove={
                                                                        tags.length > 1
                                                                            ? () =>
                                                                                  setTags(
                                                                                      tags.filter(
                                                                                          (item) => item !== tag,
                                                                                      ),
                                                                                  )
                                                                            : undefined
                                                                    }
                                                                    stroke={color?.stroke}
                                                                />
                                                            </span>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {droppableProvided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </>
                    )
                ) : (
                    <ButtonSkeleton size='small' style={{ color: token.colorTextTertiary, width: 16 * 24 }} />
                )}
            </Col>
            {showFinancialSeriesToolbar ? (
                <Col flex='none'>
                    <FinancialSeriesToolbar
                        deletingSavedSeriesId={deletingSavedSeriesId}
                        defaults={financialDefaults!}
                        loadingSeries={loadingSavedSeries}
                        savedSeries={savedSeries}
                        savingSeries={savingSeries}
                        snapshot={financialSeriesSnapshot!}
                        onApplySeries={onApplySeries!}
                        onCreateSeries={onCreateSeries!}
                        onDeleteSeries={onDeleteSeries!}
                        onUpdateSeries={onUpdateSeries!}
                        onDefaultsChange={onFinancialDefaultsChange!}
                        updatingSavedSeriesId={updatingSavedSeriesId}
                    />
                </Col>
            ) : null}
        </Row>
    );
};

export default MetricLineItems;
